import type { FunctionComponent, MouseEvent } from 'react';
import Checkbox from '@atlaskit/checkbox';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { McpPolicyIntentGroup, McpPolicyRawToolset } from '../domain/policyTypes';
import PermissionCard from '../../shared/agent-consent/PermissionCard/PermissionCard';

const cardStyle = xcss({
  borderRadius: 'radius.xxlarge',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderColor: 'color.border',
  padding: 'space.200',
});

const iconWrapStyle = xcss({ flexShrink: 0 });
const chevronWrapStyle = xcss({ flexShrink: 0, marginLeft: 'auto' });
const dropdownContentStyle = xcss({ paddingTop: 'space.200', paddingInline: 'space.0' });
const checkboxCellStyle = xcss({ paddingBlock: 'space.025' });
const toolsetsListIndentStyle = xcss({ paddingLeft: 'space.400' });

export type McpIntentPermissionCardProps = {
  group: McpPolicyIntentGroup;
  editMode?: boolean;
  selectedToolsetNames: ReadonlySet<string>;
  onToggleToolset: (toolsetName: string, selected: boolean) => void;
  onToggleSummaryCard: (selected: boolean) => void;
};

const McpIntentPermissionCard: FunctionComponent<McpIntentPermissionCardProps> = ({
  group,
  editMode = false,
  selectedToolsetNames,
  onToggleToolset,
  onToggleSummaryCard,
}) => {
  const { id, label, description, toolsets } = group;

  const selectedInCard = toolsets.filter((t) => selectedToolsetNames.has(t.name)).length;
  const intentFullySelected = toolsets.length > 0 && selectedInCard === toolsets.length;
  const intentPartiallySelected = selectedInCard > 0 && selectedInCard < toolsets.length;

  const summaryId = `mcp-summary-${id}`;

  if (!editMode) {
    return <PermissionCard title={label} description={description} />;
  }

  const stopSummaryNavigate = (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest('input[type="checkbox"]')) {
      e.stopPropagation();
    }
  };

  return (
    <Box xcss={cardStyle} role="group" aria-labelledby={`${summaryId}-title`}>
      <details>
        <summary
          style={{ listStyle: 'none', cursor: 'pointer' }}
          onClick={stopSummaryNavigate}
        >
          <Flex gap="space.200" alignItems="start" xcss={{ width: '100%' }}>
            <Box xcss={iconWrapStyle} onClick={(e: MouseEvent) => e.stopPropagation()}>
              <Checkbox
                isChecked={intentFullySelected}
                isIndeterminate={intentPartiallySelected}
                onChange={(e) => onToggleSummaryCard((e.target as HTMLInputElement).checked)}
                label=""
                name=""
                aria-label={`Select all ${label} toolsets`}
              />
            </Box>
            <Stack space="space.100">
              <Text weight="bold">{label}</Text>
              <Text as="p" size="medium">{description}</Text>
            </Stack>
            <Box xcss={chevronWrapStyle} aria-hidden>
              <ChevronDownIcon label="" size="medium" color={token('color.text.subtle')} />
            </Box>
          </Flex>
        </summary>
        <Box xcss={dropdownContentStyle}>
          <Box xcss={toolsetsListIndentStyle}>
            <Stack space="space.200">
              {toolsets.map((toolset: McpPolicyRawToolset) => (
                <Flex key={toolset.name} gap="space.200" alignItems="start">
                  <Box xcss={checkboxCellStyle} onClick={(e: MouseEvent) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={selectedToolsetNames.has(toolset.name)}
                      onChange={(e) =>
                        onToggleToolset(toolset.name, (e.target as HTMLInputElement).checked)
                      }
                      label=""
                      aria-label={`${toolset.name}: ${toolset.description}`}
                    />
                  </Box>
                  <Stack alignInline="start" space="space.050">
                    <Text as="span" weight="medium">{toolset.name}</Text>
                    <Text as="span" size="small" color="color.text.subtle">
                      {toolset.description}
                    </Text>
                  </Stack>
                </Flex>
              ))}
            </Stack>
          </Box>
        </Box>
      </details>
    </Box>
  );
};

export default McpIntentPermissionCard;
